/**
 * CohortHeatmap — weekly retention matrix (IA §3.1.4). Milestone v1.4 /
 * Plan 03-05 Task 5.3.
 *
 * Shape consumed (from /salescoach/api/engagement/cohort-heatmap):
 *   {
 *     cohortWeek: 'YYYY-MM-DD' | 'older',  // ISO Monday of the join week
 *     cohortSize: number,                   // users who first used the bot in that week
 *     retention: [{ weekOffset: 0..N, activeUsers, pct }]
 *   }[]
 *
 * The `'older'` sentinel folds all cohorts older than `maxWeeks` into one
 * compact row (the backend does this fold; we just render it as "Older").
 *
 * UX redesign (cohort-redesign):
 *   - The +0w column is dropped at render time. It is always 100% by
 *     definition (a cohort in its own join week is 100% of itself active),
 *     so it's pure visual noise. The grid starts at +1w.
 *   - The colour scale is remapped non-linearly: retention collapses fast
 *     in SaaS-style adoption curves, so we expand the visual range across
 *     0–30 % (where the action is) and compress 30–100 % at the high end.
 *     A 5 % cell looks obviously different from a 25 % cell now, whereas
 *     before both read as "very faint".
 *   - Sparse cohorts (size < 3) render with a striped "low-N" overlay and
 *     a muted text colour so a cohort-of-1's 100 % doesn't visually
 *     outshout a 50/50=100 % from a real-size cohort. Sparse rows also
 *     show a warning glyph next to their N.
 *   - The cohort size is surfaced as "N=12" in the row gutter so a 1/1
 *     never looks the same as 50/50. (It used to render as a bare number.)
 *
 * Cell background interpolates on `--primary` HSL. Tooltip uses shadcn's
 * Radix wrapper so interaction semantics match the rest of the dashboard.
 * Every cell with data is hoverable — tooltip content spells out cohort
 * week, week offset, active/size, and percentage in prose.
 */

import * as React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { isoWeekLabel, isoWeekRangeTooltip } from '@/lib/iso-week';

export interface CohortRetentionCell {
  weekOffset: number;
  activeUsers: number;
  pct: number;
}

export interface CohortRow {
  /** ISO week-start date ("YYYY-MM-DD") — or the sentinel `'older'`. */
  cohortWeek: string;
  cohortSize: number;
  retention: CohortRetentionCell[];
}

export interface CohortHeatmapProps {
  cohorts: CohortRow[];
  /** Column count (weeks since acquisition) — defaults to 12 per IA §3.1.4. */
  maxWeeks?: number;
  isLoading?: boolean;
  /**
   * Optional click handler — when present, filled cells become clickable
   * buttons that open the cohort-cell drill-down (active vs dropped users
   * for that cohort/week intersection). Empty cells stay inert since they
   * have no membership to enumerate.
   */
  onCellClick?: (cohortWeek: string, weekOffset: number) => void;
}

/**
 * Cohorts with fewer than this many members render with a striped overlay
 * and a warning glyph — a 1-of-1 = 100 % should not visually shout as loudly
 * as a 50-of-50 = 100 %.
 */
const SPARSE_COHORT_THRESHOLD = 3;

/**
 * Format a cohort row label: cohort Monday → ISO week "W{n}", `'older'`
 * sentinel → "Older". Unknown / malformed strings fall back to the raw
 * value so we never ship an empty label.
 */
function formatCohortLabel(cohortWeek: string): string {
  if (cohortWeek === 'older') return 'Older';
  return isoWeekLabel(cohortWeek);
}

/**
 * Non-linear remap emphasising the 0–30 % band.
 *
 * Real retention curves crash fast — most of the interesting signal lives
 * below 30 %. A pure-linear lerp from `--muted` to `--primary` wastes half
 * the visual range on the 50–100 % region where almost no cohort ever sits
 * past week 3.
 *
 * We spend 70 % of the saturation budget on 0–30 % (steep ramp) and the
 * remaining 30 % of the budget on 30–100 % (gentle ramp). The anchor
 * point was chosen by eyeballing the real production data — feel free to
 * tune.
 *
 * Returns a value in [0, 1] — consumer multiplies into the hsl alpha.
 */
function retentionSaturation(pct: number): number {
  const p = Math.max(0, Math.min(100, pct));
  const PIVOT_PCT = 30;
  const PIVOT_SAT = 0.7;
  if (p <= PIVOT_PCT) {
    return (p / PIVOT_PCT) * PIVOT_SAT;
  }
  return PIVOT_SAT + ((p - PIVOT_PCT) / (100 - PIVOT_PCT)) * (1 - PIVOT_SAT);
}

/**
 * Cell background — alpha-interpolates `hsl(var(--primary))` via the
 * non-linear `retentionSaturation` mapping so low-retention values are
 * visually distinguishable. Flips text to `primary-foreground` once the
 * saturation crosses 50 % so cells stay legible against the warm fill.
 */
function cellStyle(pct: number): { background: string; color: string } {
  const p = Math.max(0, Math.min(100, pct));
  // Near-zero cells render as cold muted so dead cohorts read as absent.
  if (p < 1) {
    return {
      background: 'hsl(var(--muted))',
      color: 'hsl(var(--muted-foreground))',
    };
  }
  const alpha = retentionSaturation(p).toFixed(2);
  return {
    background: `hsl(var(--primary) / ${alpha})`,
    color:
      Number(alpha) >= 0.5
        ? 'hsl(var(--primary-foreground))'
        : 'hsl(var(--foreground))',
  };
}

/**
 * A blank cell — used when the cohort has no retention entry for this week.
 * Empty cells are still hoverable so a user landing on an unfilled slot
 * gets an explanation ("this cohort hasn't reached that week yet") instead
 * of a silent no-op.
 */
function EmptyCell({
  cohortWeek,
  cohortSize,
  weekOffset,
}: {
  cohortWeek: string;
  cohortSize: number;
  weekOffset: number;
}): React.ReactElement {
  const cohortLabel =
    cohortWeek === 'older'
      ? 'Older cohorts'
      : `Cohort ${isoWeekLabel(cohortWeek)}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          data-testid="cohort-cell-empty"
          className="h-9 w-12 rounded-sm border border-border/60 bg-muted/40 cursor-default"
        />
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px] text-left">
        <div className="space-y-1 text-xs">
          <div className="font-medium">No data at +{weekOffset}w</div>
          <div className="text-muted-foreground">
            {cohortLabel} ({cohortSize} user{cohortSize === 1 ? '' : 's'})
            hasn&rsquo;t reached week +{weekOffset} yet — check back once this
            cohort ages into the window.
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * A filled cell — hoverable, with a pct label + rich tooltip breakdown.
 *
 * Relies on an ancestor `<TooltipProvider>` so we don't spin up one
 * provider per cell. Tooltip copy spells out the cohort (row), the week
 * offset (column), and the raw numerator/denominator with a plain-English
 * gloss.
 *
 * Sparse cohorts render with a diagonal-stripe overlay so a 1/1 = 100 %
 * doesn't visually outshout a real-size cell. The underlying colour still
 * reflects the pct so the overall gradient remains readable.
 */
function FilledCell({
  cell,
  cohortWeek,
  cohortSize,
  onCellClick,
}: {
  cell: CohortRetentionCell;
  cohortWeek: string;
  cohortSize: number;
  onCellClick?: (cohortWeek: string, weekOffset: number) => void;
}): React.ReactElement {
  const style = cellStyle(cell.pct);
  const pctLabel = `${Math.round(cell.pct)}%`;
  const isSparse = cohortSize < SPARSE_COHORT_THRESHOLD;
  const cohortHeader =
    cohortWeek === 'older'
      ? 'Older cohorts (folded)'
      : `Cohort ${isoWeekLabel(cohortWeek)} — ${isoWeekRangeTooltip(cohortWeek)}`;
  // Week offset N renders as "week N after join" in prose. We never render
  // offset 0 at the cell level anymore, but the tooltip copy still defends
  // against it for the data-model's sake.
  const weekPhrase =
    cell.weekOffset === 0
      ? 'their join week (100% by definition)'
      : `week +${cell.weekOffset} after joining`;
  // Sparse cohorts get a diagonal-stripe overlay so the eye de-prioritises
  // them relative to statistically meaningful cells. The overlay is a CSS
  // `repeating-linear-gradient` blended on top of the cell fill — tuned to
  // be visible without overpowering the colour mapping.
  const combinedBg = isSparse
    ? `repeating-linear-gradient(45deg, transparent 0 4px, hsl(var(--foreground) / 0.18) 4px 5px), ${style.background}`
    : style.background;
  const clickable = Boolean(onCellClick);
  const cohortLabelShort =
    cohortWeek === 'older' ? 'Older' : isoWeekLabel(cohortWeek);
  const ariaLabel = `Cohort ${cohortLabelShort}, week +${cell.weekOffset}, ${pctLabel} retention — click to drill down`;
  const commonClassName =
    'h-9 w-12 rounded-sm border border-border/80 flex items-center justify-center text-[11px] font-medium tabular-nums' +
    (clickable
      ? ' cursor-pointer hover:ring-2 hover:ring-primary/60 hover:ring-offset-1 hover:ring-offset-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-shadow'
      : ' cursor-default');
  const commonStyle = {
    background: combinedBg,
    color: style.color,
    // Slight italic tilt on sparse cells — doubles up with the
    // stripe overlay so a colour-blind reader gets a secondary
    // signal. Kept subtle so the grid doesn't jitter visually.
    fontStyle: isSparse ? ('italic' as const) : ('normal' as const),
    opacity: isSparse ? 0.85 : 1,
  };
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {clickable ? (
          <button
            type="button"
            data-testid="cohort-cell"
            data-pct={cell.pct}
            data-week-offset={cell.weekOffset}
            data-sparse={isSparse ? 'true' : 'false'}
            aria-label={ariaLabel}
            onClick={() => onCellClick?.(cohortWeek, cell.weekOffset)}
            className={commonClassName}
            style={commonStyle}
          >
            {pctLabel}
          </button>
        ) : (
          <div
            data-testid="cohort-cell"
            data-pct={cell.pct}
            data-week-offset={cell.weekOffset}
            data-sparse={isSparse ? 'true' : 'false'}
            className={commonClassName}
            style={commonStyle}
          >
            {pctLabel}
          </div>
        )}
      </TooltipTrigger>
      <TooltipContent className="max-w-[280px] text-left">
        <div className="space-y-1 text-xs">
          <div className="font-medium">{cohortHeader}</div>
          <div className="text-muted-foreground">
            {cohortSize} RM{cohortSize === 1 ? '' : 's'} joined this cohort
            {isSparse ? ' — small sample, treat with caution.' : '.'}
          </div>
          <div>
            {cell.activeUsers} of {cohortSize} active in {weekPhrase} —{' '}
            <span className="font-medium">{pctLabel}</span>.
          </div>
          {clickable && (
            <div className="pt-1 text-muted-foreground">
              Click to drilldown — see which RMs were active vs dropped.
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Colour-legend strip — shown under the grid so readers can map cell
 * shades back to percentage ranges without hovering every cell. Stops
 * mirror the non-linear `retentionSaturation` curve so the legend
 * actually reflects what the cells look like (5 %, 15 %, 30 %, 60 %,
 * 100 % — weighted toward the action band).
 */
function HeatmapLegend(): React.ReactElement {
  const stops = [0, 10, 20, 30, 60, 100];
  return (
    <div
      data-testid="cohort-heatmap-legend"
      className="flex items-center gap-2 pt-3 text-[11px] text-muted-foreground flex-wrap"
    >
      <span>Retention</span>
      <div className="flex gap-px">
        {stops.map((pct) => {
          const { background, color } = cellStyle(pct);
          return (
            <div
              key={pct}
              className="h-4 w-8 flex items-center justify-center rounded-sm border border-border/60 tabular-nums"
              style={{ background, color, fontSize: 10 }}
            >
              {pct}%
            </div>
          );
        })}
      </div>
      <span>low &rarr; high</span>
      <span className="inline-flex items-center gap-1 pl-2 border-l border-border/60">
        <span
          aria-hidden
          className="inline-block h-3 w-4 rounded-sm border border-border/60"
          style={{
            background:
              'repeating-linear-gradient(45deg, transparent 0 3px, hsl(var(--foreground) / 0.18) 3px 4px), hsl(var(--primary) / 0.4)',
          }}
        />
        small sample (N&lt;{SPARSE_COHORT_THRESHOLD})
      </span>
    </div>
  );
}

export function CohortHeatmap({
  cohorts,
  maxWeeks = 12,
  isLoading = false,
  onCellClick,
}: CohortHeatmapProps): React.ReactElement {
  /**
   * Column offsets rendered in the grid.
   *
   * cohort-redesign: the grid starts at +1w. The +0w column is dropped
   * entirely because a cohort in its own join week is 100 % of itself
   * active by construction — it carries no information and sets an
   * unfair "ceiling" that makes real weeks (1–12) look worse than they
   * are. We keep `maxWeeks` as the total horizon (1..maxWeeks inclusive)
   * so the prop semantics don't shift under existing callers; the result
   * is `maxWeeks` columns rendered, offsets 1..maxWeeks.
   */
  const columnIndexes = React.useMemo(
    () => Array.from({ length: maxWeeks }, (_, i) => i + 1),
    [maxWeeks],
  );

  // P1-02 — mobile overflow guard.
  // <main> is clipped with `overflow-x-hidden` (DashboardLayout) so we
  // can't rely on page-level scroll to reveal +5w..+12w, the legend, and
  // the N<3 chip. Instead the heatmap owns its own horizontal scroll
  // container. Matches the mobile tab-strip pattern from commit bd6cef0a:
  //   - thin scrollbar (`[scrollbar-width:thin]` + webkit thumb styled off
  //     the theme border so we don't add palette entries).
  //   - right-edge fade overlay, visible only when there is scrollable
  //     content to the right. The fade uses `--card` as the from-colour
  //     because the heatmap sits inside a <Card> on EngagementPage —
  //     from-theme-background would bleed the page bg onto the card.
  // The leading column (row label + N gutter) is sticky-left so rows stay
  // readable while the grid scrolls. The "how to read" caption lives
  // OUTSIDE the scroll container so it wraps naturally on narrow
  // viewports without stretching the scroll width.
  //
  // React error #310 fix: these hooks MUST run on every render, BEFORE
  // any early return. Previously they lived below the `isLoading` /
  // empty-cohorts early returns, so the hook count changed between the
  // loading render (0 scroll hooks) and the populated render (4 scroll
  // hooks) — a rules-of-hooks violation that surfaced as #310 in prod
  // whenever data landed after a skeleton frame.
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const [hasLeftScroll, setHasLeftScroll] = React.useState(false);
  const [hasRightScroll, setHasRightScroll] = React.useState(false);
  const recomputeOverflow = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 1px tolerance — same guard the nav strip uses for sub-pixel rounding.
    // 2026-04-24 audit: bidirectional fade. `hasLeftScroll` telegraphs that
    // there are earlier weeks to scroll back to once the reader has swiped
    // past +1w / +2w; the right-edge cue alone left the panel reading as a
    // dead-end when scrolled.
    setHasLeftScroll(el.scrollLeft > 1);
    setHasRightScroll(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  React.useEffect(() => {
    const raf = requestAnimationFrame(recomputeOverflow);
    const onResize = () => recomputeOverflow();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [recomputeOverflow, cohorts, maxWeeks]);

  if (isLoading) {
    return (
      <div data-testid="cohort-heatmap-skeleton" className="space-y-1">
        {Array.from({ length: 5 }).map((_, r) => (
          <div key={r} className="flex gap-1">
            {Array.from({ length: maxWeeks }).map((_, c) => (
              <div
                key={c}
                className="h-9 w-12 rounded-sm bg-muted animate-pulse"
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (!cohorts || cohorts.length === 0) {
    return (
      <div
        data-testid="cohort-heatmap-empty"
        className="flex items-center justify-center py-12 text-sm text-muted-foreground"
      >
        No cohorts in selected period
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={100}>
      <div data-testid="cohort-heatmap" className="relative">
        {/*
          cohort-redesign: "how to read" caption primes first-time viewers on
          what the rows/columns mean and calls out the +0w drop so analysts
          who remember the old view aren't surprised by the missing column.
          P1-02: lives outside the scroll container so it wraps cleanly at
          narrow viewports.
        */}
        <p
          data-testid="cohort-heatmap-howto"
          className="pb-2 text-[11px] leading-snug text-muted-foreground max-w-[720px]"
        >
          <span className="font-medium text-foreground">Y axis</span>: weekly
          cohort (ISO week an RM first used the bot).{' '}
          <span className="font-medium text-foreground">X axis</span>: weeks
          since acquisition (+1w, +2w, …). Each cell shows what share of the
          cohort was still active that week — darker = better retention.
          The +0w column is omitted because it is 100 % by definition. Hover
          any cell for the exact breakdown; striped cells mark cohorts with
          fewer than {SPARSE_COHORT_THRESHOLD} members (statistically thin —
          treat 100 %s there with caution).
        </p>
        <div className="relative">
          <div
            ref={scrollRef}
            data-testid="cohort-heatmap-scroll"
            onScroll={recomputeOverflow}
            className="overflow-x-auto [-webkit-overflow-scrolling:touch] [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-theme-border [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
          >
            <div className="inline-block min-w-full">
        {/*
          Column headers render +1w … +Nw. The leading label block
          (`Cohort / N`) is sticky-left so rows stay readable while the
          grid scrolls under it. Matching sticky treatment applies to
          each row's label + N-gutter below. `bg-card` supplies an
          opaque background so scrolled cells don't bleed through.
        */}
        <div className="flex gap-1 pb-1">
          <div
            aria-hidden
            className="sticky left-0 z-10 bg-card w-36 flex items-end pr-2 justify-end text-[10px] font-medium text-muted-foreground uppercase tracking-wide"
          >
            Cohort / N
          </div>
          {columnIndexes.map((w) => (
            <Tooltip key={w}>
              <TooltipTrigger asChild>
                <div
                  data-testid="cohort-col-header"
                  data-week-offset={w}
                  className="h-6 w-12 text-center text-[10px] font-medium text-muted-foreground tabular-nums cursor-default"
                >
                  +{w}w
                </div>
              </TooltipTrigger>
              <TooltipContent>
                Week {w} after the cohort joined.
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
        <div data-testid="cohort-heatmap-grid" className="space-y-1">
          {cohorts.map((row) => {
            // Index retention by weekOffset for O(1) lookup per column.
            const byOffset = new Map<number, CohortRetentionCell>();
            for (const r of row.retention) byOffset.set(r.weekOffset, r);
            const rowLabel = formatCohortLabel(row.cohortWeek);
            const rowTooltip = isoWeekRangeTooltip(row.cohortWeek);
            const isSparseRow = row.cohortSize < SPARSE_COHORT_THRESHOLD;
            return (
              <div
                key={row.cohortWeek}
                data-testid="cohort-row"
                data-cohort-week={row.cohortWeek}
                data-sparse={isSparseRow ? 'true' : 'false'}
                className="flex items-center gap-1"
              >
                {/* P1-02: row-label + N-gutter are wrapped in a sticky
                    container so they stay visible while the grid scrolls
                    horizontally. `bg-card` gives an opaque backdrop so
                    the scrolled cells don't bleed through the sticky
                    strip; `z-10` keeps it above the data cells. */}
                <div className="sticky left-0 z-10 bg-card flex items-center gap-1 pr-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      data-testid="cohort-row-label"
                      className="w-16 pr-2 text-xs font-medium text-muted-foreground tabular-nums cursor-default"
                    >
                      {rowLabel}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[260px] text-left">
                    <div className="space-y-1 text-xs">
                      <div className="font-medium">{rowTooltip}</div>
                      <div className="text-muted-foreground">
                        {row.cohortSize} RM{row.cohortSize === 1 ? '' : 's'}{' '}
                        joined the bot this week
                        {isSparseRow
                          ? ' — small sample, % cells should be read with caution.'
                          : '.'}
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
                {/*
                  Cohort size rendered as "N=12" so a reader grasping the
                  row doesn't have to guess the denominator — a 1/1 100 %
                  now visibly says "N=1" rather than a bare "1". Sparse
                  rows carry a warning glyph so the eye de-prioritises
                  them even at a glance.
                */}
                <div
                  className="w-20 pr-2 text-right text-xs font-mono tabular-nums text-muted-foreground flex items-center justify-end gap-1"
                  data-testid="cohort-size"
                >
                  {isSparseRow && (
                    <span
                      aria-label="small sample"
                      title={`Small cohort (N<${SPARSE_COHORT_THRESHOLD}) — retention % is statistically thin.`}
                      className="text-amber-600"
                    >
                      ⚠
                    </span>
                  )}
                  <span>N={row.cohortSize}</span>
                </div>
                </div>
                {columnIndexes.map((w) => {
                  const cell = byOffset.get(w);
                  return (
                    <React.Fragment key={w}>
                      {cell === undefined ? (
                        <EmptyCell
                          cohortWeek={row.cohortWeek}
                          cohortSize={row.cohortSize}
                          weekOffset={w}
                        />
                      ) : (
                        <FilledCell
                          cell={cell}
                          cohortWeek={row.cohortWeek}
                          cohortSize={row.cohortSize}
                          onCellClick={onCellClick}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })}
        </div>
            </div>
          </div>
          {/* P1-02: right-edge fade overlay — mirrors the mobile
              tab-strip pattern (commit bd6cef0a). Visible only while
              there is scrollable content to the right so the affordance
              collapses on desktop where everything fits. `bg-gradient`
              fades into `--card` because the heatmap sits in a <Card>
              on EngagementPage; using `theme-background` would look
              wrong on the card surface. */}
          {hasLeftScroll && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-card to-transparent"
            />
          )}
          {hasRightScroll && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-card to-transparent"
            />
          )}
        </div>
        <HeatmapLegend />
      </div>
    </TooltipProvider>
  );
}
